import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../hooks/useAuth';
import { useWorkspaces } from '../providers/WorkspaceProvider';
import { Button } from '../components/Button';
import { Input } from '../components/Input';
import { campaignService } from '../services/campaignService';
import type {
  Campaign,
  CampaignWorkspace,
  BufferStatusEntry,
  CampaignBufferStatus,
} from '@agency-smm/shared';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type TabKey = 'overview' | 'workspaces' | 'batches' | 'buffer';

// ---------------------------------------------------------------------------
// Styled Components
// ---------------------------------------------------------------------------

const PageWrapper = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing.lg};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSize.lg};
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing.xs};
  &:hover {
    color: ${({ theme }) => theme.colors.text};
  }
`;

const Title = styled.h1`
  font-size: ${({ theme }) => theme.fontSize.xxl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`;

const Subtitle = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textMuted};
`;

const TabBar = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xs};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const Tab = styled.button<{ $active: boolean }>`
  background: none;
  border: none;
  border-bottom: 2px solid
    ${({ $active, theme }) => ($active ? theme.colors.primary : 'transparent')};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.primary : theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s;
  &:hover {
    color: ${({ theme }) => theme.colors.text};
  }
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  padding: ${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const CardTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing.md} 0;
`;

const FormRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  align-items: flex-end;
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const Field = styled.div<{ $flex?: number }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
  flex: ${({ $flex }) => $flex ?? 1};
  min-width: 160px;
`;

const Label = styled.label`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const PlatformList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const PlatformBadge = styled.span`
  background: ${({ theme }) => theme.colors.primary}22;
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  padding: 2px 8px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
`;

const ColumnsWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.lg};

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`;

const GroupColumn = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
`;

const GroupHeader = styled.h3`
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing.sm} 0;
`;

const WorkspaceRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  &:last-child {
    border-bottom: none;
  }
`;

const WorkspaceName = styled.span`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text};
`;

const Logo = styled.img`
  width: 24px;
  height: 24px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  object-fit: cover;
`;

const SmallButton = styled.button<{ $variant?: 'danger' | 'default' }>`
  background: ${({ $variant, theme }) =>
    $variant === 'danger' ? theme.colors.error + '18' : theme.colors.surface};
  color: ${({ $variant, theme }) =>
    $variant === 'danger' ? theme.colors.error : theme.colors.textSecondary};
  border: 1px solid
    ${({ $variant, theme }) =>
      $variant === 'danger' ? theme.colors.error + '44' : theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  font-size: ${({ theme }) => theme.fontSize.xs};
  padding: 2px 8px;
  cursor: pointer;
  &:hover {
    opacity: 0.8;
  }
`;

const ActionRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${({ theme }) => theme.spacing.lg};
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.fontSize.sm};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.sm}`};
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};
`;

const Td = styled.td`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text};
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.sm}`};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const StatusBadge = styled.span<{
  $status: 'critical' | 'warning' | 'healthy' | 'pending' | 'generating' | 'completed' | 'failed';
}>`
  display: inline-block;
  font-size: ${({ theme }) => theme.fontSize.xs};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  padding: 2px 10px;
  border-radius: 999px;
  ${({ $status, theme }) => {
    switch ($status) {
      case 'critical':
      case 'failed':
        return `background: ${theme.colors.error}18; color: ${theme.colors.error};`;
      case 'warning':
      case 'pending':
      case 'generating':
        return `background: ${theme.colors.warning}22; color: ${theme.colors.warning};`;
      case 'healthy':
      case 'completed':
        return `background: ${theme.colors.success}18; color: ${theme.colors.success};`;
      default:
        return `background: ${theme.colors.border}; color: ${theme.colors.textMuted};`;
    }
  }}
`;

const ProgressBarOuter = styled.div`
  width: 100%;
  height: 8px;
  background: ${({ theme }) => theme.colors.border};
  border-radius: 4px;
  overflow: hidden;
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

const ProgressBarInner = styled.div<{ $pct: number }>`
  height: 100%;
  width: ${({ $pct }) => $pct}%;
  background: ${({ theme }) => theme.colors.primary};
  transition: width 0.3s ease;
`;

const InlineForm = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.md};
  align-items: flex-end;
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const NextGroupDue = styled.div`
  display: inline-block;
  padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.md}`};
  background: ${({ theme }) => theme.colors.primary}14;
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const ErrorBanner = styled.div`
  background: ${({ theme }) => theme.colors.error}14;
  color: ${({ theme }) => theme.colors.error};
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.fontSize.sm};
`;

const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: ${({ theme }) => theme.fontSize.md};
`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function CampaignDetailPage() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { workspaces: allWorkspaces } = useWorkspaces();

  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabKey>('overview');

  // Overview edit state
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    clientName: '',
    postsPerAccountPerWeek: 1,
    rotationCycleWeeks: 1,
    bufferMinWeeks: 1,
    bufferMaxWeeks: 4,
  });
  const [saving, setSaving] = useState(false);

  // Batch generation state
  const [showBatchForm, setShowBatchForm] = useState(false);
  const [batchGroup, setBatchGroup] = useState<'A' | 'B'>('A');
  const [batchStartDate, setBatchStartDate] = useState('');
  const [generating, setGenerating] = useState(false);
  const [genProgress, setGenProgress] = useState<{
    batchId: string;
    totalPosts: number;
    generatedPosts: number;
    currentWorkspace: string;
    currentIndex: number;
    totalWorkspaces: number;
    errors: string[];
    startTime: number;
  } | null>(null);

  // Buffer state
  const [bufferStatus, setBufferStatus] = useState<CampaignBufferStatus | null>(null);
  const [bufferLoading, setBufferLoading] = useState(false);

  // ------ Access guard ------
  if (!user || user.role !== 'super_admin') {
    return (
      <PageWrapper>
        <ErrorBanner>You do not have permission to view this page.</ErrorBanner>
      </PageWrapper>
    );
  }

  // ------ Data loading ------
  const loadCampaign = useCallback(async () => {
    if (!campaignId) return;
    try {
      setError(null);
      const data = await campaignService.get(campaignId);
      setCampaign(data);
      setEditForm({
        name: data.name,
        clientName: data.clientName || '',
        postsPerAccountPerWeek: data.postsPerAccountPerWeek,
        rotationCycleWeeks: data.rotationCycleWeeks,
        bufferMinWeeks: data.bufferMinWeeks,
        bufferMaxWeeks: data.bufferMaxWeeks,
      });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load campaign');
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    loadCampaign();
  }, [loadCampaign]);

  // ------ Buffer loading ------
  const loadBuffer = useCallback(async () => {
    if (!campaignId) return;
    setBufferLoading(true);
    try {
      const data = await campaignService.getBufferStatus(campaignId);
      setBufferStatus(data);
    } catch {
      // silent — tab will show empty
    } finally {
      setBufferLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    if (activeTab === 'buffer') {
      loadBuffer();
    }
  }, [activeTab, loadBuffer]);

  // No polling needed — client drives generation workspace by workspace

  // ------ Handlers ------
  const handleSaveOverview = async () => {
    if (!campaignId) return;
    setSaving(true);
    try {
      await campaignService.update(campaignId, editForm);
      const refreshed = await campaignService.get(campaignId);
      setCampaign(refreshed);
      setEditing(false);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleAddWorkspace = async (workspaceId: string, groupLabel: 'A' | 'B') => {
    if (!campaignId) return;
    try {
      await campaignService.addWorkspace(campaignId, workspaceId, groupLabel);
      await loadCampaign();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to add workspace');
    }
  };

  const handleRemoveWorkspace = async (workspaceId: string) => {
    if (!campaignId) return;
    try {
      await campaignService.removeWorkspace(campaignId, workspaceId);
      await loadCampaign();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to remove workspace');
    }
  };

  const handleMoveWorkspace = async (workspaceId: string, newGroup: 'A' | 'B') => {
    if (!campaignId) return;
    try {
      await campaignService.updateWorkspaceGroup(campaignId, workspaceId, newGroup);
      await loadCampaign();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to move workspace');
    }
  };

  const handleGenerateBatch = async () => {
    if (!campaignId || !batchStartDate) return;
    setGenerating(true);
    setShowBatchForm(false);
    const errors: string[] = [];

    try {
      // Step 1: Create the batch and get workspace queue
      const batch = await campaignService.generateBatch(campaignId, {
        groupLabel: batchGroup,
        targetStartDate: batchStartDate,
      });

      const progress = {
        batchId: batch.batchId,
        totalPosts: batch.totalPosts,
        generatedPosts: 0,
        currentWorkspace: batch.workspaces[0]?.workspaceName || '',
        currentIndex: 0,
        totalWorkspaces: batch.workspaces.length,
        errors: [],
        startTime: Date.now(),
      };
      setGenProgress(progress);

      // Step 2: Generate workspace by workspace
      for (let i = 0; i < batch.workspaces.length; i++) {
        const ws = batch.workspaces[i];
        setGenProgress(prev => prev ? {
          ...prev,
          currentWorkspace: ws.workspaceName,
          currentIndex: i,
        } : prev);

        try {
          const result = await campaignService.generateWorkspace(campaignId, batch.batchId, ws.workspaceId);
          setGenProgress(prev => prev ? {
            ...prev,
            generatedPosts: result.totalBatchProgress,
          } : prev);

          if (result.error) {
            errors.push(`${ws.workspaceName}: ${result.error}`);
          }
        } catch (err: unknown) {
          const msg = `${ws.workspaceName}: ${err instanceof Error ? err.message : 'Failed'}`;
          errors.push(msg);
        }
      }

      // Step 3: Mark batch as complete
      await campaignService.completeBatch(campaignId, batch.batchId, errors);
      await loadCampaign();

      if (errors.length > 0) {
        setError(`Completed with ${errors.length} error(s): ${errors[0]}`);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to generate batch');
    } finally {
      setGenerating(false);
      setGenProgress(null);
    }
  };

  const handleExport = async (batchId: string) => {
    if (!campaignId) return;
    try {
      const url = await campaignService.getExportUrl(campaignId, batchId);
      window.open(url, '_blank');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to export');
    }
  };

  // ------ Derived data ------
  const groupA = (campaign?.workspaces ?? []).filter((w) => w.groupLabel === 'A');
  const groupB = (campaign?.workspaces ?? []).filter((w) => w.groupLabel === 'B');
  const assignedIds = new Set((campaign?.workspaces ?? []).map((w) => w.workspaceId));
  const available = allWorkspaces.filter((w) => !assignedIds.has(w.id));
  const batches = [...(campaign?.batches ?? [])].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );

  // ------ Loading / Error states ------
  if (loading) {
    return (
      <PageWrapper>
        <LoadingWrapper>Loading campaign...</LoadingWrapper>
      </PageWrapper>
    );
  }

  if (!campaign) {
    return (
      <PageWrapper>
        <ErrorBanner>{error ?? 'Campaign not found'}</ErrorBanner>
        <Button onClick={() => navigate('/campaigns')}>Back to Campaigns</Button>
      </PageWrapper>
    );
  }

  // ------ Tab renderers ------

  const renderOverview = () => (
    <Card>
      <CardTitle>Campaign Settings</CardTitle>
      {editing ? (
        <>
          <FormRow>
            <Field>
              <Label>Name</Label>
              <Input
                value={editForm.name}
                onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
              />
            </Field>
            <Field>
              <Label>Client</Label>
              <Input
                value={editForm.clientName}
                onChange={(e) => setEditForm((f) => ({ ...f, clientName: e.target.value }))}
              />
            </Field>
          </FormRow>
          <FormRow>
            <Field>
              <Label>Posts / Account / Week</Label>
              <Input
                type="number"
                min={1}
                value={editForm.postsPerAccountPerWeek}
                onChange={(e) =>
                  setEditForm((f) => ({
                    ...f,
                    postsPerAccountPerWeek: parseInt(e.target.value, 10) || 1,
                  }))
                }
              />
            </Field>
            <Field>
              <Label>Rotation Cycle (weeks)</Label>
              <Input
                type="number"
                min={1}
                value={editForm.rotationCycleWeeks}
                onChange={(e) =>
                  setEditForm((f) => ({
                    ...f,
                    rotationCycleWeeks: parseInt(e.target.value, 10) || 1,
                  }))
                }
              />
            </Field>
          </FormRow>
          <FormRow>
            <Field>
              <Label>Buffer Min (weeks)</Label>
              <Input
                type="number"
                min={0}
                value={editForm.bufferMinWeeks}
                onChange={(e) =>
                  setEditForm((f) => ({
                    ...f,
                    bufferMinWeeks: parseInt(e.target.value, 10) || 0,
                  }))
                }
              />
            </Field>
            <Field>
              <Label>Buffer Max (weeks)</Label>
              <Input
                type="number"
                min={1}
                value={editForm.bufferMaxWeeks}
                onChange={(e) =>
                  setEditForm((f) => ({
                    ...f,
                    bufferMaxWeeks: parseInt(e.target.value, 10) || 1,
                  }))
                }
              />
            </Field>
          </FormRow>
          <FormRow>
            <Button onClick={handleSaveOverview} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </Button>
            <Button
              onClick={() => {
                setEditing(false);
                setEditForm({
                  name: campaign.name,
                  clientName: campaign.clientName || '',
                  postsPerAccountPerWeek: campaign.postsPerAccountPerWeek,
                  rotationCycleWeeks: campaign.rotationCycleWeeks,
                  bufferMinWeeks: campaign.bufferMinWeeks,
                  bufferMaxWeeks: campaign.bufferMaxWeeks,
                });
              }}
            >
              Cancel
            </Button>
          </FormRow>
        </>
      ) : (
        <>
          <FormRow>
            <Field>
              <Label>Name</Label>
              <span>{campaign.name}</span>
            </Field>
            <Field>
              <Label>Client</Label>
              <span>{campaign.clientName}</span>
            </Field>
          </FormRow>
          <FormRow>
            <Field>
              <Label>Posts / Account / Week</Label>
              <span>{campaign.postsPerAccountPerWeek}</span>
            </Field>
            <Field>
              <Label>Rotation Cycle</Label>
              <span>{campaign.rotationCycleWeeks} week(s)</span>
            </Field>
          </FormRow>
          <FormRow>
            <Field>
              <Label>Buffer</Label>
              <span>
                {campaign.bufferMinWeeks} - {campaign.bufferMaxWeeks} weeks
              </span>
            </Field>
            <Field>
              <Label>Platforms</Label>
              <PlatformList>
                {campaign.platforms.map((p) => (
                  <PlatformBadge key={p}>{p}</PlatformBadge>
                ))}
              </PlatformList>
            </Field>
          </FormRow>
          <FormRow>
            <Field>
              <Label>Status</Label>
              <span>{campaign.isActive ? 'Active' : 'Inactive'}</span>
            </Field>
          </FormRow>
          <Button onClick={() => setEditing(true)}>Edit Settings</Button>
        </>
      )}
    </Card>
  );

  const renderWorkspaceCard = (cw: CampaignWorkspace, otherGroup: 'A' | 'B') => (
    <WorkspaceRow key={cw.workspaceId}>
      <WorkspaceName>
        {cw.logoUrl && <Logo src={cw.logoUrl} alt="" />}
        {cw.workspaceName}
      </WorkspaceName>
      <ActionRow>
        <SmallButton onClick={() => handleMoveWorkspace(cw.workspaceId, otherGroup)}>
          Move to {otherGroup}
        </SmallButton>
        <SmallButton $variant="danger" onClick={() => handleRemoveWorkspace(cw.workspaceId)}>
          Remove
        </SmallButton>
      </ActionRow>
    </WorkspaceRow>
  );

  const renderWorkspaces = () => (
    <>
      <ColumnsWrapper>
        <GroupColumn>
          <GroupHeader>Group A ({groupA.length})</GroupHeader>
          {groupA.length === 0 && <EmptyState>No workspaces assigned</EmptyState>}
          {groupA.map((cw) => renderWorkspaceCard(cw, 'B'))}
        </GroupColumn>
        <GroupColumn>
          <GroupHeader>Group B ({groupB.length})</GroupHeader>
          {groupB.length === 0 && <EmptyState>No workspaces assigned</EmptyState>}
          {groupB.map((cw) => renderWorkspaceCard(cw, 'A'))}
        </GroupColumn>
      </ColumnsWrapper>

      {available.length > 0 && (
        <Card style={{ marginTop: '1rem' }}>
          <CardTitle>Available Workspaces</CardTitle>
          {available.map((ws) => (
            <WorkspaceRow key={ws.id}>
              <WorkspaceName>
                {(ws as { logoUrl?: string }).logoUrl && (
                  <Logo src={(ws as { logoUrl?: string }).logoUrl!} alt="" />
                )}
                {ws.name}
              </WorkspaceName>
              <ActionRow>
                <SmallButton onClick={() => handleAddWorkspace(ws.id, 'A')}>
                  Add to A
                </SmallButton>
                <SmallButton onClick={() => handleAddWorkspace(ws.id, 'B')}>
                  Add to B
                </SmallButton>
              </ActionRow>
            </WorkspaceRow>
          ))}
        </Card>
      )}
    </>
  );

  const renderBatches = () => (
    <>
      {!showBatchForm && !generating && (
        <Button onClick={() => setShowBatchForm(true)}>Generate Batch</Button>
      )}

      {showBatchForm && (
        <InlineForm>
          <Field>
            <Label>Group</Label>
            <select
              value={batchGroup}
              onChange={(e) => setBatchGroup(e.target.value as 'A' | 'B')}
              style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #ccc' }}
            >
              <option value="A">Group A</option>
              <option value="B">Group B</option>
            </select>
          </Field>
          <Field>
            <Label>Target Start Date</Label>
            <Input
              type="date"
              value={batchStartDate}
              onChange={(e) => setBatchStartDate(e.target.value)}
            />
          </Field>
          <Button onClick={handleGenerateBatch} disabled={!batchStartDate}>
            Start Generation
          </Button>
          <Button onClick={() => setShowBatchForm(false)}>Cancel</Button>
        </InlineForm>
      )}

      {generating && genProgress && (
        <Card>
          <CardTitle style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: '50%', border: '2px solid #3b82f6', borderTopColor: 'transparent', animation: 'spin 1s linear infinite' }} />
            Generating — Workspace {genProgress.currentIndex + 1}/{genProgress.totalWorkspaces}
          </CardTitle>
          <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '4px' }}>
            Currently generating: <strong>{genProgress.currentWorkspace}</strong>
          </div>
          <div style={{ fontSize: '0.875rem', marginBottom: '8px' }}>
            {genProgress.generatedPosts} / {genProgress.totalPosts} posts
          </div>
          <ProgressBarOuter>
            <ProgressBarInner
              $pct={
                genProgress.totalPosts > 0
                  ? (genProgress.generatedPosts / genProgress.totalPosts) * 100
                  : 0
              }
            />
          </ProgressBarOuter>
          <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '8px' }}>
            {(() => {
              const elapsed = (Date.now() - genProgress.startTime) / 1000;
              if (genProgress.currentIndex === 0 && genProgress.generatedPosts === 0) {
                return 'Estimated time: ~30s per workspace...';
              }
              const wsCompleted = genProgress.currentIndex;
              if (wsCompleted === 0) return 'Calculating...';
              const avgPerWs = elapsed / wsCompleted;
              const remaining = avgPerWs * (genProgress.totalWorkspaces - wsCompleted);
              const mins = Math.ceil(remaining / 60);
              return `~${mins} min remaining`;
            })()}
          </div>
          <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
        </Card>
      )}

      {generating && !genProgress && (
        <Card>
          <EmptyState>Preparing batch...</EmptyState>
        </Card>
      )}

      <Card style={{ marginTop: '1rem' }}>
        <CardTitle>Past Batches</CardTitle>
        {batches.length === 0 ? (
          <EmptyState>No batches generated yet</EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Week</Th>
                <Th>Group</Th>
                <Th>Posts</Th>
                <Th>Status</Th>
                <Th>Created</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {batches.map((b) => (
                <tr key={b.id}>
                  <Td>{b.weekLabel}</Td>
                  <Td>{b.groupLabel}</Td>
                  <Td>
                    {b.generatedPosts}/{b.totalPosts}
                  </Td>
                  <Td>
                    <StatusBadge $status={b.status}>{b.status}</StatusBadge>
                  </Td>
                  <Td>{new Date(b.createdAt).toLocaleDateString()}</Td>
                  <Td>
                    {b.status === 'completed' && (
                      <SmallButton onClick={() => handleExport(b.id)}>Export CSV</SmallButton>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  );

  const renderBuffer = () => {
    if (bufferLoading) {
      return <LoadingWrapper>Loading buffer status...</LoadingWrapper>;
    }

    if (!bufferStatus) {
      return <EmptyState>Unable to load buffer status</EmptyState>;
    }

    const { entries, nextGroupDue } = bufferStatus;

    return (
      <>
        <NextGroupDue>Next group due: {nextGroupDue}</NextGroupDue>

        <Card>
          <CardTitle>Buffer Status</CardTitle>
          {entries.length === 0 ? (
            <EmptyState>No workspaces assigned yet</EmptyState>
          ) : (
            <Table>
              <thead>
                <tr>
                  <Th>Workspace</Th>
                  <Th>Group</Th>
                  <Th>Weeks Ahead</Th>
                  <Th>Scheduled Posts</Th>
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry: BufferStatusEntry) => (
                  <tr key={entry.workspaceId}>
                    <Td>
                      <WorkspaceName>
                        {entry.logoUrl && <Logo src={entry.logoUrl} alt="" />}
                        {entry.workspaceName}
                      </WorkspaceName>
                    </Td>
                    <Td>{entry.groupLabel}</Td>
                    <Td>{entry.weeksAhead}</Td>
                    <Td>{entry.totalScheduledPosts}</Td>
                    <Td>
                      <StatusBadge $status={entry.status}>{entry.status}</StatusBadge>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      </>
    );
  };

  // ------ Main render ------
  return (
    <PageWrapper>
      <Header>
        <BackButton onClick={() => navigate('/campaigns')} title="Back to campaigns">
          &larr;
        </BackButton>
        <div>
          <Title>{campaign.name}</Title>
          <Subtitle>{campaign.clientName}</Subtitle>
        </div>
      </Header>

      {error && <ErrorBanner>{error}</ErrorBanner>}

      <TabBar>
        {(['overview', 'workspaces', 'batches', 'buffer'] as const).map((tab) => (
          <Tab key={tab} $active={activeTab === tab} onClick={() => setActiveTab(tab)}>
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </Tab>
        ))}
      </TabBar>

      {activeTab === 'overview' && renderOverview()}
      {activeTab === 'workspaces' && renderWorkspaces()}
      {activeTab === 'batches' && renderBatches()}
      {activeTab === 'buffer' && renderBuffer()}
    </PageWrapper>
  );
}
